import type { ArtStateValues } from '../core/ArtState.js';
import type { ReplayData, ReplayResult } from './ReplaySchema.js';

/**
 * Did the replay reproduce the performance?
 *
 * Deliberately not a boolean. A replay has several kinds of fidelity and they
 * fail for different reasons, so a single pass/fail would hide which one moved.
 * The game result must match exactly — it is derived from stored numbers and any
 * difference is a bug. `ArtState` is rebuilt by running the timeline through the
 * live mutation rules, so it is compared within a tolerance: it accumulates in
 * floating point across thousands of steps and is advanced by real frame deltas,
 * which are never identical twice.
 *
 * Signal and mutation counts are reported as differences rather than judged,
 * because a recipe change is *expected* to alter them — that is the point of
 * reinterpretation — and this function cannot know which mode it is being used
 * in. The caller decides what a difference means.
 */

export interface ReplayComparison {
  readonly resultMatches: boolean;
  readonly resultDifferences: readonly string[];
  /**
   * Whether the fields a replay can be held to actually match.
   *
   * Judged over the performance-driven fields only — see `PERFORMANCE_FIELDS`.
   * Null when the replay carries no recorded state to compare against.
   */
  readonly artStateMatches: boolean | null;
  readonly artStateDeltas: Readonly<Record<string, number>>;
  readonly worstArtDelta: number;
  /** The worst delta among the fields that are actually judged. */
  readonly worstPerformanceDelta: number;
  /** Reported, never judged. See `AUDIO_FIELDS`. */
  readonly audioDrivenDeltas: Readonly<Record<string, number>>;
  readonly signalCountDeltas: Readonly<Record<string, number>>;
  /**
   * Difference in dispatched mutations.
   *
   * **Not a fidelity measure.** A level signal mapped to an action fires once
   * per frame, so this number is proportional to how many frames were rendered
   * — a replay watched at a different frame rate legitimately produces a very
   * different count from the run it reproduces. Reported because it is useful
   * for spotting a recipe that has stopped firing at all.
   */
  readonly mutationCountDelta: number | null;
  readonly tolerance: number;
}

/**
 * Fields determined by what the player did.
 *
 * These are driven by discrete events — misses, breaks, presses — every one of
 * which is in the replay file. A replay that reproduces the performance must
 * reproduce these, so they are what `artStateMatches` is computed from.
 */
export const PERFORMANCE_FIELDS = ['chaos', 'distortion', 'symmetry', 'flow', 'stability'] as const;

/**
 * Fields that integrate the audio.
 *
 * `energy` and `density` accumulate `AMPLITUDE` and `BASS` over time, sampled
 * once a frame. Two runs of the same song at different frame cadences sample the
 * waveform at different instants and therefore integrate to slightly different
 * totals — with identical audio and an identical performance. That is inherent
 * to integrating a continuous signal at discrete moments, not a fidelity
 * failure, so these are reported and not judged.
 */
export const AUDIO_FIELDS = ['energy', 'density'] as const;

export interface ReplayObservation {
  readonly result?: ReplayResult;
  readonly artState?: ArtStateValues;
  readonly signalCounts?: Readonly<Record<string, number>>;
  readonly mutationCount?: number;
}

/**
 * Default tolerance for a rebuilt `ArtState` field.
 *
 * 0.20 on a 0..1 scale, and set from measurement rather than from hope. Every
 * field is advanced by wall-clock frame deltas, and several carry a
 * time-integrated term — `stability` climbs during silence, `chaos` decays
 * continuously — so a replay watched at a different frame rate lands near the
 * original rather than on it.
 *
 * Measured across the phase's end-to-end runs, with the performance reproduced
 * faithfully: `distortion` 0.019, `chaos` 0.105, `symmetry` 0.111, `stability`
 * 0.159. An earlier guess of 0.12 sat inside that spread and would have failed
 * on frame cadence alone. What the threshold must actually catch is a *missing*
 * input or event, which moves these fields by whole tenths — the tests in
 * `replay.test.ts` pin that by flipping a field to its opposite extreme.
 */
export const DEFAULT_ART_TOLERANCE = 0.2;

export function compareReplay(
  replay: ReplayData,
  observed: ReplayObservation,
  tolerance = DEFAULT_ART_TOLERANCE,
): ReplayComparison {
  const resultDifferences: string[] = [];
  if (observed.result !== undefined) {
    const expected = replay.result;
    const actual = observed.result;
    compareNumber('score', expected.score, actual.score, resultDifferences);
    compareNumber('hitCount', expected.hitCount, actual.hitCount, resultDifferences);
    compareNumber('missCount', expected.missCount, actual.missCount, resultDifferences);
    compareNumber('maxCombo', expected.maxCombo, actual.maxCombo, resultDifferences);
    compareNumber('totalEvents', expected.totalEvents, actual.totalEvents, resultDifferences);
    // Accuracy is a ratio, so it is compared as one rather than bit for bit.
    if (Math.abs(expected.accuracy - actual.accuracy) > 1e-6) {
      resultDifferences.push(`accuracy ${expected.accuracy} vs ${actual.accuracy}`);
    }
  }

  const recordedArt = replay.metadata.finalArtState;
  const artDeltas: Record<string, number> = {};
  const audioDeltas: Record<string, number> = {};
  let worst = 0;
  let worstPerformance = 0;
  let artMatches: boolean | null = null;

  if (recordedArt !== undefined && observed.artState !== undefined) {
    artMatches = true;
    const audio = new Set<string>(AUDIO_FIELDS);
    for (const key of Object.keys(recordedArt) as (keyof ArtStateValues)[]) {
      const delta = Math.abs(recordedArt[key] - observed.artState[key]);
      artDeltas[key] = Number(delta.toFixed(4));
      if (delta > worst) worst = delta;
      if (audio.has(key)) {
        audioDeltas[key] = Number(delta.toFixed(4));
        continue;
      }
      if (delta > worstPerformance) worstPerformance = delta;
      if (delta > tolerance) artMatches = false;
    }
  }

  const signalDeltas: Record<string, number> = {};
  const recordedSignals = replay.metadata.signalCounts;
  if (recordedSignals !== undefined && observed.signalCounts !== undefined) {
    const names = new Set([
      ...Object.keys(recordedSignals),
      ...Object.keys(observed.signalCounts),
    ]);
    for (const name of names) {
      const delta = (observed.signalCounts[name] ?? 0) - (recordedSignals[name] ?? 0);
      if (delta !== 0) signalDeltas[name] = delta;
    }
  }

  return {
    resultMatches: resultDifferences.length === 0,
    resultDifferences,
    artStateMatches: artMatches,
    artStateDeltas: artDeltas,
    worstArtDelta: Number(worst.toFixed(4)),
    worstPerformanceDelta: Number(worstPerformance.toFixed(4)),
    audioDrivenDeltas: audioDeltas,
    signalCountDeltas: signalDeltas,
    mutationCountDelta:
      replay.metadata.mutationCount === undefined || observed.mutationCount === undefined
        ? null
        : observed.mutationCount - replay.metadata.mutationCount,
    tolerance,
  };
}

function compareNumber(name: string, expected: number, actual: number, into: string[]): void {
  if (expected !== actual) into.push(`${name} ${expected} vs ${actual}`);
}
